import React, { useState, useEffect } from 'react'
import { Typography, Card, Table, Tag, Button, Select, Space, message, DatePicker } from 'antd'
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'

const { Title } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

const AuditLog = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [days, setDays] = useState(7)
  const [actionFilter, setActionFilter] = useState(null)

  useEffect(() => {
    fetchLogs()
  }, [days, actionFilter])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const params = { days }
      if (actionFilter) {
        params.action = actionFilter
      }

      const response = await axios.get('/system/audit-log', {
        headers: { Authorization: `Bearer ${token}` },
        params
      })
      setLogs(response.data)
    } catch (error) {
      console.error('Failed to fetch audit logs:', error)
      message.error('Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }

  const getActionColor = (action) => {
    const colors = {
      'login_success': 'green',
      'login_failed': 'red',
      'logout': 'default',
      'password_changed': 'orange',
      'admin_created': 'green',
      'admin_updated': 'orange',
      'admin_status_changed': 'purple',
      'config_updated': 'purple',
      'maintenance_mode_changed': 'orange',
    }
    return colors[action] || 'default'
  }

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'timestamp',
      width: 180,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: 'Admin',
      dataIndex: 'admin_email',
      key: 'admin',
      render: (email) => email || <Tag>System</Tag>
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color={getActionColor(action)}>
          {action.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Resource',
      key: 'resource',
      render: (_, record) => {
        if (record.resource_type && record.resource_id) {
          return `${record.resource_type}: ${record.resource_id}`
        }
        if (record.resource_type) {
          return record.resource_type
        }
        return '-'
      }
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip',
      render: (ip) => ip || '-'
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details) => {
        if (!details) return '-'
        const detailStr = JSON.stringify(details)
        return detailStr.length > 50 ? detailStr.substring(0, 50) + '...' : detailStr
      }
    }
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Audit Log</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchLogs} loading={loading}>
          Refresh
        </Button>
      </div>

      <Card className="mb-4">
        <Space>
          <span>Time Range:</span>
          <Select value={days} onChange={setDays} style={{ width: 150 }}>
            <Option value={1}>Last 24 hours</Option>
            <Option value={7}>Last 7 days</Option>
            <Option value={30}>Last 30 days</Option>
            <Option value={90}>Last 90 days</Option>
          </Select>

          <span style={{ marginLeft: 16 }}>Action:</span>
          <Select
            value={actionFilter}
            onChange={setActionFilter}
            style={{ width: 220 }}
            allowClear
            placeholder="All actions"
          >
            <Option value="login_success">Login Success</Option>
            <Option value="login_failed">Login Failed</Option>
            <Option value="logout">Logout</Option>
            <Option value="password_changed">Password Changed</Option>
            <Option value="admin_created">Admin Created</Option>
            <Option value="admin_updated">Admin Updated</Option>
            <Option value="admin_status_changed">Admin Status Changed</Option>
            <Option value="config_updated">Config Updated</Option>
            <Option value="maintenance_mode_changed">Maintenance Mode Changed</Option>
          </Select>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="log_id"
          size="small"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} entries`
          }}
        />
      </Card>
    </div>
  )
}

export default AuditLog
